import { EIntegracoesAPIEnum } from "@/features/financas/views/Integracoes/@types/IntegracoesAPIEnum";
import { ETipoVisualizacao } from "../../@types/IntegracaoCampoEnum";
import { TIntegracaoCampoModel } from "../../@types/IntegracaoCampoModel";
import {
  CadastroDeIntegracaoGateway,
  TIntegracaoAction
} from "../../services/CadastroDeIntegracaoGateway";
import useBuildLegacyPayload from "../dynamicForm/handlers/useBuildLegacyPayload/useBuildLegacyPayload";
import usePayloadResolver from "../dynamicForm/handlers/usePayloadResolver/usePayloadResolver";

export type TExecuteEditActionParams = {
  action: TIntegracaoAction;
  values: Record<string, unknown>;
  apiHash: EIntegracoesAPIEnum;
  webserviceId?: string | null;
  fields: TIntegracaoCampoModel[];
  inativo?: boolean | null;
};

const normalizeConditionValue = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

export default function useEditActionExecutor(
  gateway: CadastroDeIntegracaoGateway
) {
  const { buildLegacyPayload } = useBuildLegacyPayload();
  const { buildPayload } = usePayloadResolver();

  const conditionMatches = (
    action: TIntegracaoAction,
    values: Record<string, unknown>
  ): boolean => {
    const condition = action.when;
    if (!condition?.fieldId) return false;

    const current = normalizeConditionValue(values[condition.fieldId]);
    const expected = (condition.values ?? []).map(normalizeConditionValue);
    const contains = expected.includes(current);

    switch ((condition.operator ?? "Equals").toLowerCase()) {
      case "notequals":
      case "notin":
        return !contains;
      case "equals":
      case "in":
      default:
        return contains;
    }
  };

  const resolveAction = (
    action: TIntegracaoAction | null,
    values: Record<string, unknown>
  ): TIntegracaoAction | null => {
    if (!action || action.type !== "conditional") return action;

    return resolveAction(
      conditionMatches(action, values)
        ? (action.then ?? null)
        : (action.else ?? null),
      values
    );
  };

  const dataUrlToBlob = (dataUrl: string): Blob | null => {
    const match = /^data:([^;,]+)?(?:;base64)?,(.*)$/i.exec(dataUrl);
    if (!match) return null;

    const mimeType = match[1] || "application/octet-stream";
    const isBase64 = dataUrl.slice(0, dataUrl.indexOf(",")).includes(";base64");
    const content = isBase64 ? atob(match[2]) : decodeURIComponent(match[2]);
    const bytes = Uint8Array.from(content, (character) =>
      character.charCodeAt(0)
    );

    return new Blob([bytes], { type: mimeType });
  };

  const buildLegacyMultipartPayload = ({
    values,
    apiHash,
    webserviceId,
    fields,
    inativo = null
  }: Omit<TExecuteEditActionParams, "action">): FormData => {
    const formData = new FormData();

    if (webserviceId) formData.append("value._id", webserviceId);
    formData.append("value.API", String(apiHash));
    if (inativo !== null) formData.append("value.Inativo", String(inativo));

    fields.forEach((field) => {
      const path = field.nomeObjetoNoLegado?.trim();
      if (!path) return;

      const value = values[field.nomeCampo];
      if (field.tipoVisualizacao === ETipoVisualizacao.UPLOAD) {
        (Array.isArray(value) ? value : []).forEach((file, index) => {
          if (typeof file !== "string") return;
          const blob = dataUrlToBlob(file);
          if (blob) formData.append("anexo", blob, `anexo-${index + 1}`);
        });

        return;
      }

      if (Array.isArray(value)) {
        value.forEach((item, index) =>
          formData.append(
            `value.${path}[${index}]`,
            typeof item === "object" ? JSON.stringify(item) : String(item ?? "")
          )
        );

        return;
      }

      formData.append(
        `value.${path}`,
        typeof value === "object" ? JSON.stringify(value) : String(value ?? "")
      );
    });

    return formData;
  };

  const execute = ({
    action,
    values,
    apiHash,
    webserviceId,
    fields,
    inativo = null
  }: TExecuteEditActionParams) => {
    const actionValues = webserviceId
      ? {
          ...values,
          _id: webserviceId,
          id: webserviceId,
          Id: webserviceId
        }
      : values;
    const endpoint = (action.endpoint ?? "").replace(
      /\{([^}]+)\}/g,
      (_, key: string) =>
        encodeURIComponent(String(actionValues[key] ?? ""))
    );

    if (action.payloadStrategy === "legacyMultipart")
      return gateway.executarAcaoMultipart(
        action.method ?? "POST",
        endpoint,
        buildLegacyMultipartPayload({
          values,
          apiHash,
          webserviceId,
          fields,
          inativo
        }),
        action.host
      );

    if (action.payloadStrategy === "legacy")
      return gateway.executarAcaoCampo(
        action.method ?? "POST",
        endpoint,
        buildLegacyPayload({ apiHash, fields, values, webserviceId, inativo }),
        action.host
      );

    return gateway.executarAcaoCampo(
      action.method ?? "POST",
      endpoint,
      buildPayload(action.payload, actionValues),
      action.host
    );
  };

  return { execute, resolveAction };
}
